import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { 
  FileText, Plus, Trash2, Download, Send, Eye, ArrowRight,
  CheckCircle, Building2, User, Calendar, Euro, Printer,
  Zap, Shield, Clock, Share2, Linkedin, Twitter, Mail, X
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { analytics } from '../../lib/ga4';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const IMAGES = {
  hero: 'https://static.prod-images.emergentagent.com/jobs/e195386f-f80b-4538-af09-25780105987d/images/462416a34087b814791337cae360d8729f5bbf7444912a408f4ef8c08b7b2b11.png'
};

const OutilFactureGratuit = () => {
  const invoiceRef = useRef(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [sendByEmail, setSendByEmail] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  
  const [invoice, setInvoice] = useState({
    // Émetteur
    emetteur: {
      nom: '',
      adresse: '',
      siret: '',
      email: '',
      telephone: ''
    },
    // Client
    client: {
      nom: '',
      adresse: '',
      siret: '',
      email: ''
    },
    // Facture
    numero: `FACT-${new Date().getFullYear()}-001`,
    date: new Date().toISOString().split('T')[0],
    echeance: new Date(Date.now() + 30*24*60*60*1000).toISOString().split('T')[0],
    // Lignes
    lignes: [
      { description: '', quantite: 1, prixUnitaire: 0 }
    ],
    // TVA
    tauxTVA: 20,
    franchiseTVA: false,
    // Notes
    notes: ''
  });

  const updateEmetteur = (field, value) => {
    setInvoice(prev => ({
      ...prev,
      emetteur: { ...prev.emetteur, [field]: value }
    }));
  };

  const updateClient = (field, value) => {
    setInvoice(prev => ({
      ...prev,
      client: { ...prev.client, [field]: value }
    }));
  };

  const updateLigne = (index, field, value) => {
    const newLignes = [...invoice.lignes];
    newLignes[index][field] = field === 'quantite' || field === 'prixUnitaire' ? parseFloat(value) || 0 : value;
    setInvoice(prev => ({ ...prev, lignes: newLignes }));
  };

  const addLigne = () => {
    setInvoice(prev => ({
      ...prev,
      lignes: [...prev.lignes, { description: '', quantite: 1, prixUnitaire: 0 }]
    }));
  };

  const removeLigne = (index) => {
    if (invoice.lignes.length > 1) {
      setInvoice(prev => ({
        ...prev,
        lignes: prev.lignes.filter((_, i) => i !== index)
      }));
    }
  };

  const calculerTotaux = () => {
    const totalHT = invoice.lignes.reduce((sum, ligne) => sum + (ligne.quantite * ligne.prixUnitaire), 0);
    const montantTVA = invoice.franchiseTVA ? 0 : totalHT * (invoice.tauxTVA / 100);
    const totalTTC = totalHT + montantTVA;
    return { totalHT, montantTVA, totalTTC };
  };

  const { totalHT, montantTVA, totalTTC } = calculerTotaux();

  // Handle email capture before download
  const handleDownloadClick = () => {
    if (!emailSubmitted) {
      setShowEmailModal(true);
    } else {
      generatePDF();
    }
  };

  // Submit email and download
  const handleEmailSubmit = async (skipEmail = false) => {
    if (!skipEmail && userEmail) {
      // Track email capture
      analytics.trackEvent('invoice_tool_email_capture', { email: userEmail });
      
      // Send to backend for lead capture
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      if (API_URL) {
        try {
          await fetch(`${API_URL}/api/leads/capture`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              email: userEmail,
              source: 'invoice_tool',
              invoice_total: totalTTC
            })
          });
          
          // If user wants invoice by email, send it
          if (sendByEmail) {
            setSendingEmail(true);
            await fetch(`${API_URL}/api/leads/send-invoice`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                email: userEmail,
                invoice_number: invoice.numero,
                invoice_total: totalTTC,
                emetteur_nom: invoice.emetteur.nom,
                client_nom: invoice.client.nom
              })
            });
            setSendingEmail(false);
          }
        } catch (e) {
          setSendingEmail(false);
          // Silent fail
        }
      }
    }
    
    setEmailSubmitted(true);
    setShowEmailModal(false);
    generatePDF();
  };

  // Share functions
  const shareText = "Je viens de créer ma facture gratuitement avec SmartFacture AI ! Essayez-le :";
  const shareUrl = "https://smartfacture-ai.com/outils/facture-gratuite";
  
  const shareOnLinkedIn = () => {
    analytics.trackEvent('invoice_tool_share', { platform: 'linkedin' });
    window.open(
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
      '_blank',
      'width=600,height=400'
    );
  };
  
  const shareOnTwitter = () => {
    analytics.trackEvent('invoice_tool_share', { platform: 'twitter' });
    window.open(
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`,
      '_blank',
      'width=600,height=400'
    );
  };
  
  const shareByEmail = () => {
    analytics.trackEvent('invoice_tool_share', { platform: 'email' });
    window.location.href = `mailto:?subject=${encodeURIComponent("Outil de facturation gratuit - SmartFacture AI")}&body=${encodeURIComponent(shareText + "\n\n" + shareUrl)}`;
  };

  const generatePDF = async () => {
    analytics.trackEvent('invoice_tool_download', { format: 'pdf' });
    
    if (!invoiceRef.current) return;
    
    const canvas = await html2canvas(invoiceRef.current, { scale: 2 });
    const imgData = canvas.toDataURL('image/png');
    
    const pdf = new jsPDF('p', 'mm', 'a4');
    const imgWidth = 210;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    
    pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
    pdf.save(`${invoice.numero}.pdf`);
  };

  return (
    <>
      <Helmet>
        <title>Créer une facture gratuite en ligne (PDF) | Générateur facture freelance | SmartFacture AI</title>
        <meta name="description" content="Créez une facture professionnelle gratuite en ligne en 2 minutes. Modèle conforme pour freelances et auto-entrepreneurs. Téléchargement PDF instantané. Sans inscription." />
        <link rel="canonical" href="https://smartfacture-ai.com/outils/facture-gratuite" />
        <meta name="keywords" content="créer facture gratuite, facture freelance, facture auto entrepreneur, générateur facture PDF, facturation 2026" />
      </Helmet>

      <div className="min-h-screen bg-slate-50">
        {/* Header */}
        <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-xl flex items-center justify-center">
                <FileText className="h-5 w-5 text-white" />
              </div>
              <span className="font-bold text-xl text-slate-900">SmartFacture AI</span>
            </Link>
            <Link 
              to="/register"
              className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
            >
              Essai gratuit
            </Link>
          </div>
        </header>

        {/* Hero */}
        <section className="bg-gradient-to-br from-emerald-600 to-teal-700 text-white py-12">
          <div className="max-w-7xl mx-auto px-4 text-center">
            <Badge className="bg-white/20 text-white border-white/30 mb-4">
              100% Gratuit • Sans inscription
            </Badge>
            <h1 className="text-3xl lg:text-4xl font-bold mb-4">
              Créer une facture gratuite en ligne (PDF)
            </h1>
            <p className="text-xl text-emerald-100 max-w-2xl mx-auto">
              Générez une facture professionnelle en 2 minutes. Modèle conforme pour freelances et auto-entrepreneurs. Téléchargement PDF instantané.
            </p>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Formulaire */}
            <div className="space-y-6">
              {/* Émetteur */}
              <Card>
                <CardContent className="p-6">
                  <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <Building2 className="h-5 w-5 text-blue-600" />
                    Vos informations
                  </h2>
                  <div className="grid gap-4">
                    <div>
                      <Label>Nom / Raison sociale *</Label>
                      <Input 
                        value={invoice.emetteur.nom}
                        onChange={(e) => updateEmetteur('nom', e.target.value)}
                        placeholder="Votre nom ou entreprise"
                      />
                    </div>
                    <div>
                      <Label>Adresse *</Label>
                      <Input 
                        value={invoice.emetteur.adresse}
                        onChange={(e) => updateEmetteur('adresse', e.target.value)}
                        placeholder="Adresse complète"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>SIRET</Label>
                        <Input 
                          value={invoice.emetteur.siret}
                          onChange={(e) => updateEmetteur('siret', e.target.value)}
                          placeholder="123 456 789 00012"
                        />
                      </div>
                      <div>
                        <Label>Téléphone</Label>
                        <Input 
                          value={invoice.emetteur.telephone}
                          onChange={(e) => updateEmetteur('telephone', e.target.value)}
                          placeholder="06 12 34 56 78"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input 
                        type="email"
                        value={invoice.emetteur.email}
                        onChange={(e) => updateEmetteur('email', e.target.value)}
                        placeholder="votre@email.com"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Client */}
              <Card>
                <CardContent className="p-6">
                  <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <User className="h-5 w-5 text-blue-600" />
                    Client
                  </h2>
                  <div className="grid gap-4">
                    <div>
                      <Label>Nom du client *</Label>
                      <Input 
                        value={invoice.client.nom}
                        onChange={(e) => updateClient('nom', e.target.value)}
                        placeholder="Nom ou entreprise du client"
                      />
                    </div>
                    <div>
                      <Label>Adresse du client *</Label>
                      <Input 
                        value={invoice.client.adresse}
                        onChange={(e) => updateClient('adresse', e.target.value)}
                        placeholder="Adresse complète"
                      />
                    </div>
                    <div>
                      <Label>SIRET client (si pro)</Label>
                      <Input 
                        value={invoice.client.siret}
                        onChange={(e) => updateClient('siret', e.target.value)}
                        placeholder="Pour les clients professionnels"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Détails facture */}
              <Card>
                <CardContent className="p-6">
                  <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-blue-600" />
                    Détails de la facture
                  </h2>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label>N° Facture</Label>
                      <Input 
                        value={invoice.numero}
                        onChange={(e) => setInvoice(prev => ({ ...prev, numero: e.target.value }))}
                      />
                    </div>
                    <div>
                      <Label>Date</Label>
                      <Input 
                        type="date"
                        value={invoice.date}
                        onChange={(e) => setInvoice(prev => ({ ...prev, date: e.target.value }))}
                      />
                    </div>
                    <div>
                      <Label>Échéance</Label>
                      <Input 
                        type="date"
                        value={invoice.echeance}
                        onChange={(e) => setInvoice(prev => ({ ...prev, echeance: e.target.value }))}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Lignes */}
              <Card>
                <CardContent className="p-6">
                  <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <Euro className="h-5 w-5 text-blue-600" />
                    Prestations / Produits
                  </h2>
                  <div className="space-y-4">
                    {invoice.lignes.map((ligne, index) => (
                      <div key={index} className="flex gap-2 items-end">
                        <div className="flex-1">
                          <Label>Description</Label>
                          <Input 
                            value={ligne.description}
                            onChange={(e) => updateLigne(index, 'description', e.target.value)}
                            placeholder="Description de la prestation"
                          />
                        </div>
                        <div className="w-20">
                          <Label>Qté</Label>
                          <Input 
                            type="number"
                            min="1"
                            value={ligne.quantite}
                            onChange={(e) => updateLigne(index, 'quantite', e.target.value)}
                          />
                        </div>
                        <div className="w-28">
                          <Label>Prix HT</Label>
                          <Input 
                            type="number"
                            min="0"
                            step="0.01"
                            value={ligne.prixUnitaire}
                            onChange={(e) => updateLigne(index, 'prixUnitaire', e.target.value)}
                          />
                        </div>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => removeLigne(index)}
                          disabled={invoice.lignes.length === 1}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    ))}
                    <Button variant="outline" onClick={addLigne} className="w-full">
                      <Plus className="h-4 w-4 mr-2" />
                      Ajouter une ligne
                    </Button>
                  </div>

                  {/* TVA */}
                  <div className="mt-6 pt-6 border-t border-slate-200">
                    <div className="flex items-center gap-4 mb-4">
                      <label className="flex items-center gap-2">
                        <input 
                          type="checkbox"
                          checked={invoice.franchiseTVA}
                          onChange={(e) => setInvoice(prev => ({ ...prev, franchiseTVA: e.target.checked }))}
                          className="rounded"
                        />
                        <span className="text-sm">Franchise en base de TVA (auto-entrepreneur)</span>
                      </label>
                    </div>
                    {!invoice.franchiseTVA && (
                      <div className="w-32">
                        <Label>Taux TVA (%)</Label>
                        <Input 
                          type="number"
                          value={invoice.tauxTVA}
                          onChange={(e) => setInvoice(prev => ({ ...prev, tauxTVA: parseFloat(e.target.value) || 0 }))}
                        />
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* CTA Before Download */}
              <Card className="bg-gradient-to-r from-blue-600 to-cyan-500 border-none">
                <CardContent className="p-5 text-white">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center shrink-0">
                      <Zap className="h-6 w-6" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold mb-1">
                        Automatisez votre facturation avec SmartFacture AI
                      </h3>
                      <p className="text-blue-100 text-sm">
                        Factur-X 2026, relances auto, scanner IA
                      </p>
                    </div>
                    <Link 
                      to="/register"
                      className="inline-flex items-center px-4 py-2 bg-white text-blue-600 hover:bg-blue-50 font-semibold rounded-lg transition-colors shrink-0"
                      data-testid="cta-before-download-btn"
                    >
                      Essai gratuit
                    </Link>
                  </div>
                </CardContent>
              </Card>

              {/* Actions */}
              <div className="flex gap-4">
                <Button 
                  onClick={() => setShowPreview(true)}
                  variant="outline"
                  className="flex-1"
                >
                  <Eye className="h-4 w-4 mr-2" />
                  Aperçu
                </Button>
                <Button 
                  onClick={handleDownloadClick}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  data-testid="download-pdf-btn"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Télécharger PDF
                </Button>
              </div>

              {/* Share Section */}
              <Card className="bg-gradient-to-r from-blue-50 to-cyan-50 border-blue-200">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-slate-900">Partagez cet outil gratuit</p>
                      <p className="text-sm text-slate-600">Aidez d'autres entrepreneurs !</p>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={shareOnLinkedIn}
                        className="bg-[#0077B5] hover:bg-[#006396] text-white border-none"
                        data-testid="share-linkedin-btn"
                      >
                        <Linkedin className="h-4 w-4" />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={shareOnTwitter}
                        className="bg-black hover:bg-gray-800 text-white border-none"
                        data-testid="share-twitter-btn"
                      >
                        <Twitter className="h-4 w-4" />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={shareByEmail}
                        className="bg-slate-600 hover:bg-slate-700 text-white border-none"
                        data-testid="share-email-btn"
                      >
                        <Mail className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Aperçu Facture */}
            <div className="lg:sticky lg:top-24 h-fit">
              <Card className="overflow-hidden">
                <CardContent className="p-0">
                  <div className="bg-slate-100 px-4 py-3 border-b flex items-center justify-between">
                    <span className="font-medium text-slate-700">Aperçu de votre facture</span>
                    <Button size="sm" variant="ghost" onClick={generatePDF}>
                      <Printer className="h-4 w-4" />
                    </Button>
                  </div>
                  
                  {/* Facture Preview */}
                  <div ref={invoiceRef} className="bg-white p-8" style={{ minHeight: '600px' }}>
                    {/* Header Facture */}
                    <div className="flex justify-between mb-8">
                      <div>
                        <h3 className="text-xl font-bold text-slate-900">{invoice.emetteur.nom || 'Votre nom'}</h3>
                        <p className="text-slate-600 text-sm mt-1">{invoice.emetteur.adresse || 'Votre adresse'}</p>
                        {invoice.emetteur.siret && <p className="text-slate-500 text-xs mt-1">SIRET: {invoice.emetteur.siret}</p>}
                        {invoice.emetteur.email && <p className="text-slate-500 text-xs">{invoice.emetteur.email}</p>}
                        {invoice.emetteur.telephone && <p className="text-slate-500 text-xs">{invoice.emetteur.telephone}</p>}
                      </div>
                      <div className="text-right">
                        <h2 className="text-2xl font-bold text-blue-600">FACTURE</h2>
                        <p className="text-slate-600 mt-1">{invoice.numero}</p>
                        <p className="text-slate-500 text-sm">Date: {invoice.date}</p>
                        <p className="text-slate-500 text-sm">Échéance: {invoice.echeance}</p>
                      </div>
                    </div>

                    {/* Client */}
                    <div className="mb-8 p-4 bg-slate-50 rounded-lg">
                      <p className="text-xs text-slate-500 mb-1">FACTURER À</p>
                      <p className="font-semibold text-slate-900">{invoice.client.nom || 'Nom du client'}</p>
                      <p className="text-slate-600 text-sm">{invoice.client.adresse || 'Adresse du client'}</p>
                      {invoice.client.siret && <p className="text-slate-500 text-xs">SIRET: {invoice.client.siret}</p>}
                    </div>

                    {/* Tableau */}
                    <table className="w-full mb-8">
                      <thead>
                        <tr className="border-b-2 border-slate-200">
                          <th className="text-left py-2 text-slate-600 text-sm">Description</th>
                          <th className="text-right py-2 text-slate-600 text-sm w-20">Qté</th>
                          <th className="text-right py-2 text-slate-600 text-sm w-28">Prix HT</th>
                          <th className="text-right py-2 text-slate-600 text-sm w-28">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invoice.lignes.map((ligne, index) => (
                          <tr key={index} className="border-b border-slate-100">
                            <td className="py-3 text-slate-900">{ligne.description || '—'}</td>
                            <td className="py-3 text-right text-slate-600">{ligne.quantite}</td>
                            <td className="py-3 text-right text-slate-600">{ligne.prixUnitaire.toFixed(2)} €</td>
                            <td className="py-3 text-right text-slate-900 font-medium">{(ligne.quantite * ligne.prixUnitaire).toFixed(2)} €</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {/* Totaux */}
                    <div className="flex justify-end">
                      <div className="w-64">
                        <div className="flex justify-between py-2 border-b border-slate-100">
                          <span className="text-slate-600">Total HT</span>
                          <span className="font-medium">{totalHT.toFixed(2)} €</span>
                        </div>
                        {!invoice.franchiseTVA && (
                          <div className="flex justify-between py-2 border-b border-slate-100">
                            <span className="text-slate-600">TVA ({invoice.tauxTVA}%)</span>
                            <span className="font-medium">{montantTVA.toFixed(2)} €</span>
                          </div>
                        )}
                        <div className="flex justify-between py-3 bg-blue-50 px-3 rounded-lg mt-2">
                          <span className="font-bold text-slate-900">Total TTC</span>
                          <span className="font-bold text-blue-600 text-lg">{totalTTC.toFixed(2)} €</span>
                        </div>
                      </div>
                    </div>

                    {/* Mentions légales */}
                    {invoice.franchiseTVA && (
                      <p className="text-xs text-slate-500 mt-8 italic">
                        TVA non applicable, article 293 B du Code général des impôts
                      </p>
                    )}
                    <p className="text-xs text-slate-500 mt-4">
                      En cas de retard de paiement, une pénalité de 3 fois le taux d'intérêt légal sera appliquée, 
                      ainsi qu'une indemnité forfaitaire de 40€ pour frais de recouvrement.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* CTA Section */}
          <section className="mt-16 bg-gradient-to-r from-blue-600 to-cyan-500 rounded-2xl p-8 lg:p-12 text-white text-center">
            <Zap className="h-12 w-12 mx-auto mb-4 opacity-90" />
            <h2 className="text-2xl lg:text-3xl font-bold mb-4">
              Besoin de plus ? Passez à SmartFacture AI
            </h2>
            <p className="text-blue-100 mb-8 max-w-2xl mx-auto">
              Automatisez vos factures, relancez les impayés, et soyez conforme à la réforme 2026. 
              Tout ça à partir de 14€/mois.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/register"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-blue-600 hover:bg-blue-50 font-semibold rounded-xl transition-colors"
              >
                Essai gratuit 7 jours
                <ArrowRight className="h-5 w-5" />
              </Link>
              <Link 
                to="/demo"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-white text-white hover:bg-white/10 font-semibold rounded-xl transition-colors"
              >
                Voir la démo
              </Link>
            </div>
            
            {/* Avantages */}
            <div className="flex flex-wrap items-center justify-center gap-8 mt-8 text-sm">
              <span className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5" />
                Factur-X conforme 2026
              </span>
              <span className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Relances automatiques
              </span>
              <span className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Scanner IA intégré
              </span>
            </div>
          </section>

          {/* FAQ */}
          <section className="mt-16">
            <h2 className="text-2xl font-bold text-slate-900 text-center mb-8">
              Questions fréquentes
            </h2>
            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {[
                {
                  q: "Cet outil est-il vraiment gratuit ?",
                  a: "Oui, 100% gratuit et sans inscription. Vous pouvez créer et télécharger autant de factures que vous voulez."
                },
                {
                  q: "Les factures sont-elles conformes ?",
                  a: "Oui, les factures générées contiennent toutes les mentions légales obligatoires en France."
                },
                {
                  q: "Puis-je utiliser cet outil en tant qu'auto-entrepreneur ?",
                  a: "Absolument ! Cochez simplement la case 'Franchise TVA' pour ajouter la mention légale correspondante."
                },
                {
                  q: "Quelle est la différence avec SmartFacture AI ?",
                  a: "SmartFacture AI offre l'automatisation complète : Factur-X, relances automatiques, scanner IA, archivage légal, et plus encore."
                }
              ].map((faq, i) => (
                <div key={i} className="bg-white rounded-xl p-6 shadow-sm">
                  <h3 className="font-semibold text-slate-900 mb-2">{faq.q}</h3>
                  <p className="text-slate-600 text-sm">{faq.a}</p>
                </div>
              ))}
            </div>
          </section>

          {/* SEO Content Section */}
          <section className="mt-16 max-w-4xl mx-auto">
            {/* Comment faire une facture */}
            <div className="bg-white rounded-2xl p-8 shadow-sm mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">
                Comment faire une facture ?
              </h2>
              <p className="text-slate-600 mb-4">
                Créer une facture professionnelle est simple avec notre générateur gratuit. Voici les étapes essentielles :
              </p>
              <ol className="list-decimal list-inside space-y-2 text-slate-600 mb-4">
                <li><strong>Renseignez vos informations</strong> : nom, adresse, SIRET (si applicable)</li>
                <li><strong>Ajoutez les données client</strong> : nom et adresse de facturation</li>
                <li><strong>Détaillez vos prestations</strong> : description, quantité et prix unitaire</li>
                <li><strong>Vérifiez la TVA</strong> : taux applicable ou mention franchise TVA</li>
                <li><strong>Téléchargez votre PDF</strong> : facture conforme prête à envoyer</li>
              </ol>
              <p className="text-slate-600">
                Une facture doit obligatoirement contenir : la date d'émission, un numéro unique, les coordonnées des parties, le détail des prestations, les montants HT et TTC, et les conditions de paiement.
              </p>
            </div>

            {/* Facture auto-entrepreneur */}
            <div className="bg-white rounded-2xl p-8 shadow-sm mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">
                Facture auto-entrepreneur
              </h2>
              <p className="text-slate-600 mb-4">
                En tant qu'<strong>auto-entrepreneur</strong> (micro-entrepreneur), vous bénéficiez de la <strong>franchise en base de TVA</strong> tant que votre chiffre d'affaires reste sous les seuils légaux. Votre facture doit obligatoirement mentionner :
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                <p className="text-amber-800 text-sm italic">
                  "TVA non applicable, article 293 B du Code général des impôts"
                </p>
              </div>
              <p className="text-slate-600 mb-4">
                Notre outil ajoute automatiquement cette mention lorsque vous cochez la case "Franchise TVA". Les factures auto-entrepreneur doivent également inclure :
              </p>
              <ul className="list-disc list-inside space-y-1 text-slate-600">
                <li>Votre numéro SIRET</li>
                <li>La mention "EI" ou "Entreprise individuelle"</li>
                <li>Un numéro de facture séquentiel</li>
                <li>Les délais de paiement (30 jours par défaut)</li>
              </ul>
            </div>

            {/* Facture freelance */}
            <div className="bg-white rounded-2xl p-8 shadow-sm mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">
                Facture freelance
              </h2>
              <p className="text-slate-600 mb-4">
                Les <strong>freelances</strong> et travailleurs indépendants ont des obligations de facturation spécifiques. Que vous soyez développeur, graphiste, consultant ou rédacteur, votre facture doit être professionnelle et conforme.
              </p>
              <p className="text-slate-600 mb-4">
                Points essentiels pour une facture freelance :
              </p>
              <ul className="list-disc list-inside space-y-1 text-slate-600 mb-4">
                <li><strong>Numérotation continue</strong> : FACT-2026-001, FACT-2026-002...</li>
                <li><strong>Description précise</strong> des prestations réalisées</li>
                <li><strong>Dates de prestation</strong> si différentes de la date de facture</li>
                <li><strong>Conditions de règlement</strong> et pénalités de retard</li>
                <li><strong>RIB ou IBAN</strong> pour faciliter le paiement</li>
              </ul>
              <p className="text-slate-600">
                Conseil : conservez vos factures pendant 10 ans minimum pour respecter les obligations légales françaises.
              </p>
            </div>

            {/* Conformité facturation 2026 */}
            <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-2xl p-8 border border-blue-200">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">
                Conformité facturation électronique 2026
              </h2>
              <p className="text-slate-600 mb-4">
                À partir de <strong>septembre 2026</strong>, toutes les entreprises françaises devront être capables de recevoir des factures électroniques au format normalisé. L'obligation d'émettre des e-factures sera progressive :
              </p>
              <ul className="list-disc list-inside space-y-1 text-slate-600 mb-4">
                <li><strong>Septembre 2026</strong> : réception obligatoire pour tous</li>
                <li><strong>Septembre 2026</strong> : émission obligatoire pour les grandes entreprises</li>
                <li><strong>Septembre 2027</strong> : émission obligatoire pour les ETI et PME</li>
                <li><strong>Septembre 2028</strong> : émission obligatoire pour les TPE et micro-entreprises</li>
              </ul>
              <p className="text-slate-600 mb-6">
                Le format <strong>Factur-X</strong> (PDF/A-3 + XML) devient la norme. Notre outil gratuit vous permet de créer des factures PDF classiques, mais pour être 100% conforme à la réforme, découvrez SmartFacture AI qui génère automatiquement des factures Factur-X.
              </p>
              <Link 
                to="/blog/facturation-electronique-2026-guide-complet"
                className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium"
              >
                Lire notre guide complet sur la réforme 2026
                <ArrowRight className="h-4 w-4 ml-1" />
              </Link>
            </div>
          </section>
        </div>

        {/* Footer */}
        <footer className="bg-slate-900 text-white py-8 mt-16">
          <div className="max-w-7xl mx-auto px-4 text-center">
            <p className="text-slate-400">© 2026 SmartFacture AI. Tous droits réservés.</p>
          </div>
        </footer>

        {/* Email Capture Modal */}
        {showEmailModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-md w-full p-6 relative animate-in fade-in zoom-in duration-200">
              <button 
                onClick={() => setShowEmailModal(false)}
                className="absolute top-4 right-4 text-slate-400 hover:text-slate-600"
              >
                <X className="h-5 w-5" />
              </button>
              
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Download className="h-8 w-8 text-emerald-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">
                  Votre facture est prête !
                </h3>
                <p className="text-slate-600 text-sm">
                  Téléchargez votre facture PDF ou recevez-la par email.
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="capture-email">Votre email</Label>
                  <Input 
                    id="capture-email"
                    type="email"
                    value={userEmail}
                    onChange={(e) => setUserEmail(e.target.value)}
                    placeholder="votre@email.com"
                    className="mt-1"
                    data-testid="email-capture-input"
                  />
                </div>

                {/* Option recevoir par email */}
                {userEmail && (
                  <label className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg border border-blue-200 cursor-pointer">
                    <input 
                      type="checkbox"
                      checked={sendByEmail}
                      onChange={(e) => setSendByEmail(e.target.checked)}
                      className="rounded text-blue-600"
                      data-testid="send-by-email-checkbox"
                    />
                    <div>
                      <span className="text-sm font-medium text-slate-900">Recevoir la facture par email</span>
                      <p className="text-xs text-slate-500">Une copie sera envoyée à {userEmail}</p>
                    </div>
                  </label>
                )}

                <Button 
                  onClick={() => handleEmailSubmit(false)}
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                  data-testid="submit-email-btn"
                  disabled={sendingEmail}
                >
                  {sendingEmail ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                      Envoi en cours...
                    </>
                  ) : (
                    <>
                      <Download className="h-4 w-4 mr-2" />
                      {userEmail && sendByEmail ? "Envoyer + Télécharger" : userEmail ? "Recevoir conseils + Télécharger" : "Télécharger ma facture"}
                    </>
                  )}
                </Button>

                <button 
                  onClick={() => handleEmailSubmit(true)}
                  className="w-full text-sm text-slate-500 hover:text-slate-700 underline"
                  data-testid="skip-email-btn"
                >
                  Télécharger sans email
                </button>
              </div>

              <p className="text-xs text-slate-400 text-center mt-4">
                Pas de spam. Désinscription en 1 clic.
              </p>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default OutilFactureGratuit;
